import { Device, type types } from 'mediasoup-client';
import { SignalClient } from '../connection/SignalClient.js';
import type { MediaKind, ProducerInfo, TransportParameters } from '../types/index.js';
import { toError } from '../utils/browser.js';

interface ConsumerRecord {
  consumer: types.Consumer;
  producerId: string;
  userId: string;
}

export interface PeerConnectionCallbacks {
  onRemoteStream(userId: string, stream: MediaStream): void;
  onProducerClosed(data: { userId: string; producerId: string; consumerId?: string }): void;
  onError(error: Error): void;
}

/**
 * Owns the mediasoup-client Device and its send/receive transports.
 * The name is kept as PeerConnection for the SDK API, although rtc-server uses
 * an SFU and mediasoup creates the browser RTCPeerConnections internally.
 */
export class PeerConnection {
  private device: Device | null = null;
  private sendTransport: types.Transport | null = null;
  private recvTransport: types.Transport | null = null;
  private readonly producers = new Map<MediaKind, types.Producer>();
  private readonly consumers = new Map<string, ConsumerRecord>();
  private readonly consuming = new Set<string>();
  private readonly remoteStreams = new Map<string, MediaStream>();
  private roomId: string | null = null;

  constructor(
    private readonly signal: SignalClient,
    private readonly callbacks: PeerConnectionCallbacks
  ) {}

  get ready(): boolean {
    return Boolean(this.device?.loaded && this.sendTransport && this.recvTransport && this.roomId);
  }

  async initialize(roomId: string): Promise<void> {
    this.close();
    this.roomId = roomId;

    try {
      const routerRtpCapabilities = await this.signal.getRouterRtpCapabilities(roomId);
      const device = await Device.factory();
      await device.load({ routerRtpCapabilities: routerRtpCapabilities as types.RtpCapabilities });
      this.device = device;

      const [sendOptions, recvOptions] = await Promise.all([
        this.signal.createWebRtcTransport(roomId, 'send'),
        this.signal.createWebRtcTransport(roomId, 'recv')
      ]);
      this.sendTransport = device.createSendTransport(this.toTransportOptions(sendOptions));
      this.recvTransport = device.createRecvTransport(this.toTransportOptions(recvOptions));
      this.bindSendTransport(this.sendTransport, roomId);
      this.bindReceiveTransport(this.recvTransport, roomId);
    } catch (error) {
      this.close();
      throw toError(error, 'Failed to initialize WebRTC transports');
    }
  }

  async publishTrack(track: MediaStreamTrack): Promise<types.Producer> {
    if (!this.sendTransport || !this.roomId) throw new Error('WebRTC send transport is not ready');
    const kind = track.kind as MediaKind;
    const existing = this.producers.get(kind);
    if (existing && !existing.closed) {
      await existing.replaceTrack({ track });
      return existing;
    }

    const producer = await this.sendTransport.produce({
      track,
      stopTracks: false,
      disableTrackOnPause: false,
      zeroRtpOnPause: true,
      appData: { source: kind }
    });
    this.producers.set(kind, producer);
    producer.on('transportclose', () => this.producers.delete(kind));
    return producer;
  }

  async replaceVideoTrack(track: MediaStreamTrack): Promise<void> {
    const producer = this.producers.get('video');
    if (producer && !producer.closed) await producer.replaceTrack({ track });
    else await this.publishTrack(track);
  }

  async setProducerPaused(kind: MediaKind, paused: boolean): Promise<void> {
    const producer = this.producers.get(kind);
    if (!producer || producer.closed || producer.paused === paused) return;
    if (paused) producer.pause();
    else producer.resume();
  }

  async closeProducer(kind: MediaKind): Promise<void> {
    const producer = this.producers.get(kind);
    if (!producer || producer.closed) return;
    if (this.roomId) await this.signal.closeProducer(this.roomId, producer.id);
    producer.close();
    this.producers.delete(kind);
  }

  async consume(info: ProducerInfo): Promise<void> {
    if (this.consumers.has(info.producerId) || this.consuming.has(info.producerId)) return;
    if (!this.device?.loaded || !this.recvTransport || !this.roomId) {
      throw new Error('WebRTC receive transport is not ready');
    }

    this.consuming.add(info.producerId);
    try {
      const parameters = await this.signal.consume(
        this.roomId,
        this.recvTransport.id,
        info.producerId,
        this.device.recvRtpCapabilities
      );
      const consumer = await this.recvTransport.consume({
        id: parameters.id,
        producerId: parameters.producerId,
        kind: parameters.kind,
        rtpParameters: parameters.rtpParameters as types.RtpParameters,
        appData: { userId: info.userId }
      });
      const record: ConsumerRecord = { consumer, producerId: info.producerId, userId: info.userId };
      this.consumers.set(info.producerId, record);
      consumer.on('transportclose', () => this.removeConsumer(info.producerId, false));
      consumer.on('trackended', () => this.removeConsumer(info.producerId, true));

      const stream = this.remoteStreams.get(info.userId) ?? new MediaStream();
      stream.addTrack(consumer.track);
      this.remoteStreams.set(info.userId, stream);
      await this.signal.resumeConsumer(this.roomId, consumer.id);
      consumer.resume();
      this.callbacks.onRemoteStream(info.userId, stream);
    } catch (error) {
      throw toError(error, `Failed to consume producer ${info.producerId}`);
    } finally {
      this.consuming.delete(info.producerId);
    }
  }

  closeRemoteProducer(producerId: string): boolean {
    return this.removeConsumer(producerId, false);
  }

  removeUser(userId: string): void {
    for (const [producerId, record] of [...this.consumers]) {
      if (record.userId === userId) this.removeConsumer(producerId, false);
    }
    this.remoteStreams.delete(userId);
  }

  getRemoteStream(userId: string): MediaStream | null {
    return this.remoteStreams.get(userId) ?? null;
  }

  getAllRemoteStreams(): Map<string, MediaStream> {
    return new Map(this.remoteStreams);
  }

  close(): void {
    for (const { consumer } of this.consumers.values()) {
      if (!consumer.closed) consumer.close();
    }
    for (const producer of this.producers.values()) {
      if (!producer.closed) producer.close();
    }
    this.sendTransport?.close();
    this.recvTransport?.close();
    this.consumers.clear();
    this.producers.clear();
    this.consuming.clear();
    this.remoteStreams.clear();
    this.sendTransport = null;
    this.recvTransport = null;
    this.device = null;
    this.roomId = null;
  }

  private bindSendTransport(transport: types.Transport, roomId: string): void {
    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      void this.signal.connectTransport(roomId, transport.id, dtlsParameters).then(callback).catch(errback);
    });
    transport.on('produce', ({ kind, rtpParameters }, callback, errback) => {
      void this.signal
        .produce(roomId, transport.id, kind, rtpParameters)
        .then(({ id }) => callback({ id }))
        .catch(errback);
    });
    transport.on('connectionstatechange', (state) => {
      if (state === 'failed' || state === 'disconnected') {
        this.callbacks.onError(new Error(`WebRTC send transport ${state}`));
      }
    });
  }

  private bindReceiveTransport(transport: types.Transport, roomId: string): void {
    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      void this.signal.connectTransport(roomId, transport.id, dtlsParameters).then(callback).catch(errback);
    });
    transport.on('connectionstatechange', (state) => {
      if (state === 'failed' || state === 'disconnected') {
        this.callbacks.onError(new Error(`WebRTC receive transport ${state}`));
      }
    });
  }

  private removeConsumer(producerId: string, notify: boolean): boolean {
    const record = this.consumers.get(producerId);
    if (!record) return false;
    this.consumers.delete(producerId);
    if (!record.consumer.closed) record.consumer.close();

    const stream = this.remoteStreams.get(record.userId);
    stream?.removeTrack(record.consumer.track);
    if (stream && stream.getTracks().length === 0) this.remoteStreams.delete(record.userId);
    else if (stream) this.callbacks.onRemoteStream(record.userId, stream);

    if (notify) {
      this.callbacks.onProducerClosed({
        userId: record.userId,
        producerId,
        consumerId: record.consumer.id
      });
    }
    return true;
  }

  private toTransportOptions(parameters: TransportParameters): types.TransportOptions {
    return parameters as types.TransportOptions;
  }
}
